import { api } from '@/lib/api';

export interface UnifiedInvitation {
  _id: string;
  email: string;
  role: 'Admin' | 'Vendor' | 'Contractor' | 'Client' | 'Member';
  token: string;
  expiresAt: string;
  acceptedAt?: string;
  declinedAt?: string;
  invitedBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  isNewUser: boolean;
  projects: Array<{
    projectId: {
      _id: string;
      name: string;
      city: string;
      state: string;
    };
    role: string;
    acceptedAt?: string;
    declinedAt?: string;
  }>;
  isGlobalInvite: boolean;
  // Type is derived from isGlobalInvite: "global" if true, "project" if false
  type: 'global' | 'project';
  // For project invitations, the project field contains the first/primary project
  project?: {
    _id: string;
    name: string;
    city: string;
    state: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateGlobalInviteRequest {
  email: string;
  role: 'Admin' | 'Vendor' | 'Contractor' | 'Client' | 'Member';
}

export interface CreateProjectInviteRequest {
  email: string;
  role: 'Admin' | 'Vendor' | 'Contractor' | 'Client' | 'Member';
  projectIds: string[];
}

export interface ValidateInvitationResponse {
  invitationId: string;
  email: string;
  role: string;
  isGlobalInvite: boolean;
  projects: Array<{
    projectId: string;
    projectName: string;
    role: string;
    city: string;
    state: string;
  }>;
  expiresAt: string;
  isNewUser: boolean;
}

export interface AcceptInvitationRequest {
  token: string;
  projectIds?: string[];
}

export const unifiedInvitationService = {
  // Get all invitations with filtering
  getAllInvitations: async (params?: {
    isGlobalInvite?: boolean;
    projectId?: string;
    status?: 'pending' | 'accepted' | 'expired';
    page?: number;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    
    if (params?.isGlobalInvite !== undefined) {
      queryParams.append('isGlobalInvite', params.isGlobalInvite.toString());
    }
    if (params?.projectId) {
      queryParams.append('projectId', params.projectId);
    }
    if (params?.status) {
      queryParams.append('status', params.status);
    }
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }

    const queryString = queryParams.toString();
    const url = `/unified-invitations${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get<{
      invitations: UnifiedInvitation[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(url);
    return response.data;
  },

  // Get all unified invitations (both global and project)
  getAllUnifiedInvitations: async () => {
    const response = await api.get('/unified-invitations');
    return response.data.invitations || response.data;
  },

  // Get global invitations (uses unified endpoint)
  getGlobalInvitations: async () => {
    const response = await api.get('/unified-invitations?isGlobalInvite=true');
    return response.data.invitations || response.data;
  },

  // Get project invitations (legacy compatibility)
  getProjectInvitations: async (projectId?: string) => {
    if (projectId) {
      // Use the new unified invitation endpoint for specific project
      const response = await api.get(`/projects/${projectId}/invitations`);
      return response.data;
    } else {
      // Get all unified invitations filtered for project invitations
      const response = await api.get('/unified-invitations?isGlobalInvite=false');
      return response.data.invitations || response.data;
    }
  },

  // Create global invitation
  createGlobalInvitation: async (data: CreateGlobalInviteRequest) => {
    // Use the bulk invite users endpoint which handles both global and project invitations
    const response = await api.post<{ message: string; invitationId: string; token: string }>('/users/bulk-invite', {
      emails: [data.email],
      role: data.role,
      projectIds: [], // Empty array for global invitations
    });
    return response.data;
  },

  // Create project invitation
  createProjectInvitation: async (data: CreateProjectInviteRequest) => {
    // Use the bulk invite users endpoint which handles both global and project invitations
    const response = await api.post<{ message: string; invitationId: string; token: string }>('/users/bulk-invite', {
      emails: [data.email],
      role: data.role,
      projectIds: data.projectIds || [],
    });
    return response.data;
  },

  // Validate invitation token
  validateInvitation: async (token: string) => {
    const response = await api.get<ValidateInvitationResponse>(`/unified-invitations/validate/${token}`);
    return response.data;
  },

  // Accept invitation
  acceptInvitation: async (data: AcceptInvitationRequest) => {
    const response = await api.post<{ message: string; userId: string }>('/unified-invitations/accept', data);
    return response.data;
  },

  // Delete/cancel invitation
  deleteInvitation: async (invitationId: string) => {
    const response = await api.delete<{ message: string }>(`/unified-invitations/${invitationId}`);
    return response.data;
  },

  // Delete specific project from invitation
  deleteProjectFromInvitation: async (invitationId: string, projectId: string) => {
    const response = await api.delete<{ message: string; entireInvitationDeleted?: boolean; remainingProjects?: number }>(`/invitations/${invitationId}/projects/${projectId}`);
    return response.data;
  },

  // Resend invitation
  resendInvitation: async (invitationId: string) => {
    const response = await api.post<{ message: string; token: string; expiresAt: string }>(`/unified-invitations/${invitationId}/resend`);
    return response.data;
  },

  // Get invitation by ID
  getInvitationById: async (invitationId: string) => {
    const response = await api.get<UnifiedInvitation>(`/unified-invitations/${invitationId}`);
    return response.data;
  },

  // Legacy compatibility methods for global invitations
  deleteGlobalInvitation: async (invitationId: string) => {
    return unifiedInvitationService.deleteInvitation(invitationId);
  },

  resendGlobalInvitation: async (invitationId: string) => {
    return unifiedInvitationService.resendInvitation(invitationId);
  },

  // Legacy compatibility methods for project invitations  
  deleteProjectInvitation: async (invitationId: string) => {
    return unifiedInvitationService.deleteInvitation(invitationId);
  },

  resendProjectInvitation: async (invitationId: string) => {
    return unifiedInvitationService.resendInvitation(invitationId);
  },

  // Legacy compatibility method for inviting user to single project
  inviteUserToProject: async (projectId: string, data: { email: string; role: string }) => {
    const inviteData: CreateProjectInviteRequest = {
      email: data.email,
      role: data.role as 'Admin' | 'Vendor' | 'Contractor' | 'Client' | 'Member',
      projectIds: [projectId]
    };
    const response = await unifiedInvitationService.createProjectInvitation(inviteData);
    return {
      invitationId: response.invitationId,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
      email: data.email,
      role: data.role,
      projectId: projectId,
      isNewUser: true // This will be determined by the backend
    };
  },

  // Get pending invitations for current user
  getPendingInvitations: async () => {
    const response = await api.get('/pending-invitations');
    return response.data;
  },

  // Accept a specific project invitation
  acceptProjectInvitation: async (invitationId: string, projectId: string) => {
    const response = await api.post('/project-invitations/accept', {
      invitationId,
      projectId,
    });
    return response.data;
  },

  // Accept all pending invitations
  acceptAllInvitations: async () => {
    const response = await api.post('/invitations/accept-all');
    return response.data;
  },
};
